import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useState, useRef } from "react";
import { Box, Container, Text, Flex, Grid, GridItem, Image, Button, Icon, useBreakpointValue, } from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import { formatFileName } from "../common/util";
import { IMG_URL } from "../common/Constant";
import { MdArrowUpward } from "react-icons/md";
import { appendTrackingParams } from "../../shared-utils/trackingUtils";
import { ZILLOUT_WEB_BASE_URL } from "../../config/urls";
const DEFAULT_COLORS = {
    primary: "#8155FE",
    text: "white",
};
const MenuSection = ({ menuImage, menuCategories, pubName, pubColors, appContext = "customer", _pubId, }) => {
    var _a, _b;
    const { pubId: urlPubId } = useParams();
    const pubId = React.useMemo(() => {
        return urlPubId || _pubId || "";
    }, [urlPubId, _pubId]);
    const navigate = useNavigate();
    const colors = {
        primary: (_a = pubColors === null || pubColors === void 0 ? void 0 : pubColors.primary) !== null && _a !== void 0 ? _a : DEFAULT_COLORS.primary,
        text: (_b = pubColors === null || pubColors === void 0 ? void 0 : pubColors.text) !== null && _b !== void 0 ? _b : DEFAULT_COLORS.text,
    };
    const [imageMap, setImageMap] = useState({});
    const [displayedItems, setDisplayedItems] = useState([]);
    const [flippingIndex, setFlippingIndex] = useState(null);
    const [nextItem, setNextItem] = useState(null);
    const [featuredItem, setFeaturedItem] = useState(null);
    const [nextFeaturedItem, setNextFeaturedItem] = useState(null);
    const [isFeaturedFlipping, setIsFeaturedFlipping] = useState(false);
    const [hasFeaturedAnimated, setHasFeaturedAnimated] = useState(false);
    const displayedItemsRef = useRef([]);
    const gridRef = useRef(null);
    const urlPrefix = process.env.REACT_APP_ASSETS_PREFIX_URL;
    // Get responsive column count
    const columns = useBreakpointValue({ base: 3, md: 4, lg: 5, xl: 6 }) || 3;
    // Build list of all items (categories + sub-categories)
    const allItems = React.useMemo(() => {
        if (!menuCategories)
            return [];
        const items = [];
        // Add categories
        Object.keys(menuCategories).forEach((category) => {
            items.push({
                key: category,
                type: "category",
            });
        });
        // Add sub-categories
        Object.keys(menuCategories).forEach((category) => {
            const subCategories = menuCategories[category];
            if (Array.isArray(subCategories)) {
                subCategories.forEach((subCategory) => {
                    items.push({
                        key: subCategory,
                        type: "subcategory",
                        category: category,
                    });
                });
            }
        });
        return items;
    }, [menuCategories]);
    // Get items with loaded images
    const itemsWithImages = React.useMemo(() => {
        return allItems.filter((item) => imageMap[item.key]);
    }, [allItems, imageMap]);
    // Initialize displayed items based on column count - only items with images
    useEffect(() => {
        if (itemsWithImages.length > 0) {
            // Initialize or adjust based on column count
            const currentCount = displayedItems.length;
            if (currentCount === 0) {
                // Initial load - only show items with loaded images
                const initial = itemsWithImages.slice(0, columns);
                setDisplayedItems(initial);
                displayedItemsRef.current = initial;
            }
            else if (currentCount !== columns) {
                // Column count changed (responsive breakpoint)
                const newItems = itemsWithImages.slice(0, columns);
                setDisplayedItems(newItems);
                displayedItemsRef.current = newItems;
            }
        }
    }, [itemsWithImages, columns, displayedItems.length]);
    // Update displayed items when new images load (fill up to column count and replace items without images)
    useEffect(() => {
        if (itemsWithImages.length === 0)
            return;
        setDisplayedItems((prev) => {
            // Filter out items without images
            const itemsWithLoadedImages = prev.filter((item) => imageMap[item.key]);
            const currentKeys = new Set(itemsWithLoadedImages.map((item) => `${item.key}-${item.type}-${item.category || ""}`));
            // Get available items that aren't currently displayed
            const availableItems = itemsWithImages.filter((item) => !currentKeys.has(`${item.key}-${item.type}-${item.category || ""}`));
            // If we have items without images or need to fill up to column count
            if (itemsWithLoadedImages.length < columns && availableItems.length > 0) {
                const needed = columns - itemsWithLoadedImages.length;
                const toAdd = availableItems.slice(0, needed);
                const updated = [...itemsWithLoadedImages, ...toAdd];
                displayedItemsRef.current = updated;
                return updated;
            }
            else if (itemsWithLoadedImages.length < prev.length) {
                // Some items lost their images, update the list
                displayedItemsRef.current = itemsWithLoadedImages;
                return itemsWithLoadedImages;
            }
            return prev;
        });
    }, [itemsWithImages, columns, imageMap]);
    // Initialize featured item (next to main menu image)
    useEffect(() => {
        if (itemsWithImages.length > 0 && !featuredItem) {
            // Pick a random item from items with images
            const randomItem = itemsWithImages[Math.floor(Math.random() * itemsWithImages.length)];
            setFeaturedItem(randomItem);
        }
    }, [itemsWithImages, featuredItem]);
    // Shuffle featured item periodically
    useEffect(() => {
        if (itemsWithImages.length <= 1 || !featuredItem)
            return;
        const shuffleInterval = setInterval(() => {
            // Get available items (excluding current featured item)
            const availableItems = itemsWithImages.filter((item) => !(item.key === featuredItem.key &&
                item.type === featuredItem.type &&
                item.category === featuredItem.category));
            if (availableItems.length === 0)
                return;
            // Pick a new random item
            const newItem = availableItems[Math.floor(Math.random() * availableItems.length)];
            // Set next item and trigger flip
            setNextFeaturedItem(newItem);
            setIsFeaturedFlipping(true);
            // After animation completes, update the featured item
            setTimeout(() => {
                setFeaturedItem(newItem);
                setIsFeaturedFlipping(false);
                setNextFeaturedItem(null);
            }, 1200); // Full animation duration
        }, 5000); // Shuffle every 5 seconds
        return () => clearInterval(shuffleInterval);
    }, [itemsWithImages, featuredItem]);
    // Update ref when displayedItems changes
    useEffect(() => {
        displayedItemsRef.current = displayedItems;
    }, [displayedItems]);
    // Shuffle mechanism - randomly replace one displayed item
    useEffect(() => {
        if (itemsWithImages.length <= columns || displayedItems.length === 0)
            return;
        const shuffleInterval = setInterval(() => {
            const current = displayedItemsRef.current;
            // Get items not currently displayed and that have images loaded
            const availableItems = itemsWithImages.filter((item) => !current.some((curr) => curr.key === item.key &&
                curr.type === item.type &&
                curr.category === item.category));
            if (availableItems.length === 0)
                return;
            // Randomly pick one of the displayed items to replace
            const indexToReplace = Math.floor(Math.random() * current.length);
            // Randomly pick a new item from available ones
            const newItem = availableItems[Math.floor(Math.random() * availableItems.length)];
            // Set the next item to show on the back side
            setNextItem(newItem);
            // Trigger flip animation
            setFlippingIndex(indexToReplace);
            // After animation completes, update the displayed items
            setTimeout(() => {
                setDisplayedItems((prev) => {
                    const updated = [...prev];
                    updated[indexToReplace] = newItem;
                    displayedItemsRef.current = updated;
                    return updated;
                });
                setFlippingIndex(null);
                setNextItem(null);
            }, 1200); // Full animation duration
        }, 4000); // Shuffle every 4 seconds
        return () => clearInterval(shuffleInterval);
    }, [itemsWithImages, displayedItems.length, columns, imageMap]);
    // Fetch images for all items (categories + sub-categories)
    useEffect(() => {
        if (!allItems.length || !pubId)
            return;
        allItems.forEach((item) => {
            var _a;
            let s3ImageUrl;
            if (item.type === "category") {
                // Category image: ${urlPrefix}/${pubId}/${category}.png
                const categoryFileName = item.key.toLowerCase().replace(/\s+/g, "_");
                s3ImageUrl = `${urlPrefix}/${pubId}/${encodeURIComponent(categoryFileName)}.png`;
            }
            else {
                // Sub-category image: ${urlPrefix}/${pubId}/${category}/${subCategory}.png
                const categoryFileName = ((_a = item.category) === null || _a === void 0 ? void 0 : _a.toLowerCase().replace(/\s+/g, "_")) || "";
                const subCategoryFileName = item.key.toLowerCase().replace(/\s+/g, "_");
                s3ImageUrl = `${urlPrefix}/${pubId}/${encodeURIComponent(categoryFileName)}/${encodeURIComponent(subCategoryFileName)}.png`;
            }
            const imgElement = new window.Image();
            imgElement.src = s3ImageUrl;
            imgElement.onload = () => setImageMap((prev) => ({ ...prev, [item.key]: s3ImageUrl }));
            // No fallback for sub-categories, only for categories
            if (item.type === "category") {
                imgElement.onerror = () => {
                    const fileName = formatFileName(item.key);
                    setImageMap((prev) => ({
                        ...prev,
                        [item.key]: `${IMG_URL}/category_img/${fileName}.png`,
                    }));
                };
            }
            else {
                // For sub-categories, if image fails to load, we don't add it to imageMap
                // This means it won't be included in itemsWithImages
                imgElement.onerror = () => {
                    // Don't add to imageMap - item will be filtered out
                };
            }
        });
    }, [allItems, pubId, urlPrefix]);
    const handleGoToMenu = () => {
        if (appContext === "experience") {
            window.open(appendTrackingParams(`${ZILLOUT_WEB_BASE_URL}/pub/detail/menu/overview/${pubId}`), "_self");
        }
        else {
            if (pubName && pubId) {
                navigate(`/pub/detail/menu/overview/${pubName}/${pubId}`);
            }
            else if (pubId) {
                navigate(`/pub/detail/menu/overview/${pubId}`);
            }
        }
    };
    // Don't render if no menu data
    if (!menuImage && displayedItems.length === 0) {
        return null;
    }
    // Create grid template columns based on responsive value
    const gridTemplateColumns = `repeat(${columns}, 1fr)`;
    return (_jsx(Box, { w: "100%", py: { base: 8, md: 12 }, position: "relative", zIndex: 10, children: _jsx(Container, { maxW: "container.xl", px: 0, children: _jsxs(Flex, { flexDirection: "column", children: [_jsxs(Flex, { w: "100%", justifyContent: "space-between", gap: 4, children: [_jsxs(Box, { pl: 6, pr: 5, py: 4, w: "auto", children: [_jsx(Text, { fontSize: ["2xl", "2xl", "4xl", "5xl"], color: colors.text, lineHeight: "1.5", mb: 2, maxW: "500px", textTransform: "uppercase", children: "DISCOVER EVERY DISH & DRINK WE OFFER." }), _jsxs(Button, { bg: colors.primary, color: "white", borderRadius: "4", px: 10, size: "sm", w: "fit-content", h: "40px", fontSize: "xs", fontWeight: "500", _hover: { bg: colors.primary }, _active: { bg: colors.primary }, onClick: handleGoToMenu, children: [_jsx(Icon, { as: MdArrowUpward, transform: "rotate(45deg)", boxSize: 6, color: "white", mr: 1 }), "GO TO MENU"] })] }), _jsxs(Flex, { alignItems: "flex-start", children: [_jsx(Box, { position: "relative", overflow: "hidden", cursor: "pointer", onClick: handleGoToMenu, _hover: { opacity: 0.9 }, transition: "opacity 0.2s", w: ["182px", "182px", "182px", "280px"], minW: ["182px", "182px", "182px", "280px"], h: { base: "256px", md: "300px", lg: "400px" }, children: _jsx(Image, { src: menuImage || `${IMG_URL}/icons/menu_overview.webp`, alt: "Menu", w: "100%", h: "100%", objectFit: "cover" }) }), featuredItem && imageMap[featuredItem.key] && (_jsx(Box, { position: "relative", display: ["none", "none", "block", "block"], overflow: "hidden", cursor: "pointer", onClick: handleGoToMenu, _hover: { opacity: 0.9 }, w: ["182px", "182px", "182px", "280px"], minW: ["182px", "182px", "182px", "280px"], h: { base: "256px", md: "300px", lg: "400px" }, style: {
                                            perspective: "1000px",
                                        }, children: _jsxs(Box, { position: "relative", w: "100%", h: "100%", style: {
                                                transformStyle: "preserve-3d",
                                                transition: isFeaturedFlipping
                                                    ? "transform 1.2s ease-in-out"
                                                    : "none",
                                                transform: isFeaturedFlipping
                                                    ? "rotateY(180deg)"
                                                    : "rotateY(0deg)",
                                            }, children: [_jsx(Box, { position: "absolute", w: "100%", h: "100%", style: {
                                                        WebkitBackfaceVisibility: "hidden",
                                                        backfaceVisibility: "hidden",
                                                        transform: "rotateY(0deg)",
                                                    }, children: _jsx(Image, { src: imageMap[featuredItem.key], alt: featuredItem.key, w: "100%", h: "100%", objectFit: "cover" }) }), _jsx(Box, { position: "absolute", w: "100%", h: "100%", style: {
                                                        WebkitBackfaceVisibility: "hidden",
                                                        backfaceVisibility: "hidden",
                                                        transform: "rotateY(180deg)",
                                                    }, children: nextFeaturedItem && imageMap[nextFeaturedItem.key] ? (_jsx(Image, { src: imageMap[nextFeaturedItem.key], alt: nextFeaturedItem.key, w: "100%", h: "100%", objectFit: "cover" })) : (
                                                    // Show the same image on back during initial state (will be hidden)
                                                    _jsx(Image, { src: imageMap[featuredItem.key], alt: featuredItem.key, w: "100%", h: "100%", objectFit: "cover" })) })] }) }))] })] }), displayedItems.length > 0 && itemsWithImages.length >= columns && (_jsx(Box, { ref: gridRef, children: _jsx(Grid, { templateColumns: gridTemplateColumns, w: "100%", children: displayedItems
                                .filter((item) => imageMap[item.key]) // Only render items with loaded images
                                .map((item, filteredIndex) => {
                                // Find original index for flip animation
                                const originalIndex = displayedItems.findIndex((d) => d.key === item.key &&
                                    d.type === item.type &&
                                    d.category === item.category);
                                const image = imageMap[item.key];
                                const isFlipping = flippingIndex === originalIndex;
                                const newImage = isFlipping && nextItem ? imageMap[nextItem.key] : null;
                                return (_jsx(GridItem, { position: "relative", overflow: "hidden", cursor: "pointer", onClick: handleGoToMenu, _hover: { opacity: 0.9 }, h: { base: "150px", md: "250px", lg: "300px" }, style: {
                                        perspective: "1000px",
                                    }, children: _jsxs(Box, { position: "relative", w: "100%", h: "100%", style: {
                                            transformStyle: "preserve-3d",
                                            transition: "transform 1.2s ease-in-out",
                                            transform: isFlipping
                                                ? "rotateY(180deg)"
                                                : "rotateY(0deg)",
                                        }, children: [_jsx(Box, { position: "absolute", w: "100%", h: "100%", style: {
                                                    WebkitBackfaceVisibility: "hidden",
                                                    backfaceVisibility: "hidden",
                                                    transform: "rotateY(0deg)",
                                                }, children: _jsx(Image, { src: image, alt: item.key, w: "100%", h: "100%", objectFit: "cover" }) }), _jsx(Box, { position: "absolute", w: "100%", h: "100%", style: {
                                                    WebkitBackfaceVisibility: "hidden",
                                                    backfaceVisibility: "hidden",
                                                    transform: "rotateY(180deg)",
                                                }, children: newImage ? (_jsx(Image, { src: newImage, alt: (nextItem === null || nextItem === void 0 ? void 0 : nextItem.key) || "", w: "100%", h: "100%", objectFit: "cover" })) : (_jsx(Box, { w: "100%", h: "100%", bg: "#1a1a1a" })) })] }) }, `${item.key}-${item.type}-${item.category || ""}-${originalIndex}`));
                            }) }) }))] }) }) }));
};
export default MenuSection;
